<template>
  <div class="payment-detail-container">
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>收付款明细</span>
      </div>
      <div class="header-actions">
        <el-button size="small" icon="el-icon-back" @click="goBack">返回列表</el-button>
      </div>
    </div>

    <div class="content">
      <!-- 台账基本信息 -->
      <el-card class="info-card">
        <div slot="header" class="card-header">台账基本信息</div>
        <el-descriptions border :column="3">
          <el-descriptions-item label="台账编号">{{ ledgerInfo.ledgerNumber }}</el-descriptions-item>
          <el-descriptions-item label="合同名称">{{ ledgerInfo.contractName }}</el-descriptions-item>
          <el-descriptions-item label="合同编号">{{ ledgerInfo.contractNumber }}</el-descriptions-item>
          <el-descriptions-item label="供应商">{{ ledgerInfo.supplierUnitName }}</el-descriptions-item>
          <el-descriptions-item label="需求方">{{ ledgerInfo.demandUnitName }}</el-descriptions-item>
          <el-descriptions-item label="项目名称">{{ ledgerInfo.projectName }}</el-descriptions-item>
          <el-descriptions-item label="结算单号">{{ ledgerInfo.settlementNumber }}</el-descriptions-item>
          <el-descriptions-item label="结算金额" :span="2">
            <span class="amount-highlight">{{ formatAmount(ledgerInfo.settlementAmount) }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="备注" :span="3">{{ ledgerInfo.remark }}</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 结算单流水信息 -->
      <el-card class="flows-card">
        <div slot="header" class="card-header">
          <span>结算单流水信息</span>
          <span class="flow-summary">共 {{ flowList.length }} 条流水</span>
        </div>
        <el-table :data="flowList" border size="small">
          <el-table-column type="index" label="序号" width="60" align="center" />
          <el-table-column label="收付款时间" align="center">
            <template slot-scope="scope">
              {{ formatDateTime(scope.row.paymentDate) }}
            </template>
          </el-table-column>
          <el-table-column label="资金类型" align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.fundType === 'PAYABLE' ? 'warning' : 'success'" size="small">
                {{ scope.row.fundType === 'PAYABLE' ? '付款' : '收款' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="流水金额" align="center">
            <template slot-scope="scope">
              <span class="amount-text">{{ formatAmount(scope.row.amount) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="应付日期" align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.dueDate) }}
            </template>
          </el-table-column>
          <el-table-column label="逾期天数" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.overdueDays > 0" type="danger" size="small">
                {{ scope.row.overdueDays }} 天
              </el-tag>
              <span v-else class="no-overdue">0 天</span>
            </template>
          </el-table-column>
          <el-table-column label="延期费用" align="center">
            <template slot-scope="scope">
              <span class="interest-text">{{ formatAmount(scope.row.interest) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="支付方式" align="center">
            <template slot-scope="scope">
              {{ scope.row.paymentMethod || '-' }}
            </template>
          </el-table-column>
          <el-table-column label="备注" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ scope.row.remark || '-' }}
            </template>
          </el-table-column>
        </el-table>
        <div class="total-interest">
          <span class="label">延期费用合计：</span>
          <span class="value">{{ formatAmount(totalInterest) }}</span>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaymentDetail',
  data() {
    return {
      settlementId: null,
      // Demo 台账基本信息
      ledgerInfo: {
        ledgerNumber: 'TZ202601001',
        contractName: '钢材采购框架合同',
        contractNumber: 'HT-2026-001',
        supplierUnitName: 'XX钢铁贸易有限公司',
        demandUnitName: 'XX建设集团有限公司',
        projectName: '城市轨道三期工程',
        settlementNumber: 'JS202601001',
        settlementAmount: 1258000.00,
        remark: 'Demo 台账数据'
      },
      // Demo 流水数据（含逾期天数、延期费用）
      flowList: [
        {
          id: 1,
          paymentDate: '2026-01-10 10:00:00',
          fundType: 'PAYABLE',
          amount: 500000.00,
          dueDate: '2026-01-15',
          overdueDays: 0,
          interest: 0,
          paymentMethod: '银行转账',
          remark: '首笔预付款'
        },
        {
          id: 2,
          paymentDate: '2026-01-28 14:30:00',
          fundType: 'PAYABLE',
          amount: 400000.00,
          dueDate: '2026-01-20',
          overdueDays: 8,
          interest: 3280.00,
          paymentMethod: '银行转账',
          remark: '第二期款项'
        },
        {
          id: 3,
          paymentDate: '2026-02-05 09:15:00',
          fundType: 'PAYABLE',
          amount: 358000.00,
          dueDate: '2026-02-01',
          overdueDays: 4,
          interest: 5280.50,
          paymentMethod: '承兑汇票',
          remark: '尾款结算'
        }
      ]
    }
  },
  computed: {
    totalInterest() {
      return this.flowList.reduce((sum, row) => sum + parseFloat(row.interest || 0), 0)
    }
  },
  created() {
    const id = this.$route.params.id
    if (id) {
      this.settlementId = id
      this.initFromQuery()
    } else {
      this.$message.warning('缺少结算单ID')
      this.goBack()
    }
  },
  methods: {
    initFromQuery() {
      const { settlementNumber, settlementTitle } = this.$route.query
      if (settlementNumber) this.ledgerInfo.settlementNumber = settlementNumber
      if (settlementTitle) this.ledgerInfo.contractName = settlementTitle
    },
    goBack() {
      this.$router.push({ path: '/finance/interest-calculation' })
    },
    formatDateTime(str) {
      if (!str) return '-'
      const d = new Date(str)
      if (isNaN(d.getTime())) return str
      return d.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/\//g, '-')
    },
    formatDate(str) {
      if (!str) return '-'
      const d = new Date(str)
      if (isNaN(d.getTime())) return str
      return d.toLocaleDateString('zh-CN').replace(/\//g, '-')
    },
    formatAmount(amount) {
      if (amount === undefined || amount === null) return '0.00'
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  }
}
</script>

<style lang="scss" scoped>
.payment-detail-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 84px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 4px;

  .header-title {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: bold;

    .title-mark {
      width: 4px;
      height: 18px;
      background: #409eff;
      margin-right: 10px;
    }
  }
}

.content {
  min-height: 200px;
}

.info-card,
.flows-card {
  margin-bottom: 20px;
  background: #fff;

  .card-header {
    font-size: 16px;
    font-weight: bold;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .flow-summary {
      font-size: 13px;
      font-weight: normal;
      color: #909399;
    }
  }
}

.amount-highlight,
.amount-text {
  color: #f56c6c;
  font-weight: 600;
}

.interest-text {
  color: #e6a23c;
  font-weight: 600;
}

.no-overdue {
  color: #67c23a;
}

.total-interest {
  margin-top: 16px;
  padding: 12px 16px;
  background: #fef0f0;
  border-radius: 4px;
  text-align: right;

  .label {
    font-size: 14px;
    color: #606266;
  }

  .value {
    font-size: 18px;
    font-weight: bold;
    color: #f56c6c;
    margin-left: 8px;
  }
}
</style>
